import { useEffect, useRef, useState } from "react";

import getAccessToken from "./getAccessToken";

const { REACT_APP_CRONTU_API_URL, REACT_APP_CRONTU_URL } = process.env;

// Índice de cartillas de Crontu en localStorage:
//   {
//     updatedAt: 1754...,                                   → última actualización del índice
//     items: {
//       "resources/abc": { slug, url, updatedAt: 1754... }   → y de cada profesional
//     }
//   }
// El link ya no se lee de resource.externalMarketplaceUrl: se consulta a Crontu, que es
// la fuente de verdad, y se cachea acá para resolverlo sin volver a pedirlo.
export const STORAGE_KEY = "crontu";

// Cada media hora se revisa el índice y se vuelve a consultar lo que haya vencido. El
// mismo valor sirve de vigencia de las entradas y de intervalo del refresco.
export const REFRESH_MS = 1000 * 60 * 30;

// De a cuántos profesionales se consulta en paralelo, para no disparar una request por
// profesional de golpe en equipos grandes.
const BATCH = 5;

// El índice, los ids en vuelo y los suscriptores viven en el módulo y no en el hook.
// En la pantalla de profesionales conviven dos instancias montadas a la vez —el listado
// y, al abrir el menú de cuenta, el navbar—: con una copia por instancia consultarían
// los mismos profesionales dos veces y la escritura de una pisaría la de la otra.
const listeners = new Set();
const pending = new Set();
let cache = null;

// El formato anterior del índice era plano: { "resources/x": {...} }. Se descarta en vez
// de migrarlo: es un cache con vencimiento, se vuelve a llenar solo en la primera visita.
const read = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return parsed?.items && typeof parsed.items === "object" ? parsed.items : {};
  } catch {
    return {};
  }
};

const getItems = () => {
  if (!cache) cache = read();
  return cache;
};

// Sin instancias montadas no hay a quién avisarle, y resolver el índice acá volvería a
// llenar el cache justo después de haberlo descartado. `value` permite avisar de un
// índice puntual sin tocar el cache — lo usa clearIndex, que necesita dejarlo descartado.
const notify = (value) => {
  if (!listeners.size) return;
  const next = value ?? getItems();
  listeners.forEach((listener) => listener(next));
};

// Se relee localStorage antes de mergear para no pisar lo que hayan escrito otra
// instancia u otra pestaña desde la última lectura.
const writeItems = (fresh) => {
  cache = { ...read(), ...fresh };
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ updatedAt: Date.now(), items: cache })
    );
  } catch {
    // localStorage lleno o deshabilitado: el índice sigue vivo en memoria.
  }
  notify();
};

// Descarta el índice en memoria y en disco. El `storage` event no se dispara en la
// misma ventana que lo escribió, así que borrar localStorage a mano no alcanza para que
// las instancias montadas se enteren. `cache = null` hace que la próxima lectura vuelva
// a resolver desde localStorage. Por eso se avisa con un índice vacío explícito: si se
// dejara que notify lo resolviera, getItems volvería a llenar el cache —con {}— y la
// próxima lectura ya no pasaría por localStorage.
export const clearIndex = () => {
  cache = null;
  pending.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nada que hacer: el índice ya quedó descartado en memoria.
  }
  notify({});
};

// Otra pestaña actualizó el índice: se adopta su versión en vez de seguir con la propia.
// Va a nivel de módulo para registrar un solo listener, no uno por instancia del hook.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    // key === null es un localStorage.clear().
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    cache = read();
    notify();
  });
}

// Misma consulta que usa el perfil de Crontu (App/Setup/Resources/Form/Crontu).
const fetchProfile = async (resourceID, accessToken) => {
  const url = `${REACT_APP_CRONTU_API_URL}/professionals/by-external-id?externalId=${encodeURIComponent(resourceID)}`;
  let res;
  try {
    res = await fetch(url, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
  } catch {
    // fetch() LANZA (no devuelve un status) ante error de red, DNS o un
    // preflight de CORS rechazado. Antes esto burbujeaba hasta el .catch del
    // caller, que devolvía null — y null NO se cachea, así que cada
    // remount/cambio de deps volvía a consultar a los mismos profesionales.
    // Con la API de Crontu caída o mal apuntada eso se convertía en una
    // tormenta real: 255 requests en ~30s por página (medido en el trace del
    // Regression 31958707259, 2026-08-16), suficiente para que el Calendario
    // nunca llegara a networkidle y re-renderizara sin parar. La falla se
    // cachea IGUAL que un 404 (sin link, mismo TTL): como mucho un retry por
    // profesional cada 30 minutos.
    return { slug: null, url: "" };
  }

  // 404 = el profesional todavía no tiene perfil en Crontu. Se guarda igual, con url
  // vacía, para no repetir la consulta hasta el próximo vencimiento.
  if (!res.ok) return { slug: null, url: "" };

  const data = await res.json().catch(() => null);
  const slug = data?.slug || null;

  return { slug, url: slug ? `${REACT_APP_CRONTU_URL}/${slug}` : "" };
};

// Consulta a Crontu por los profesionales que falten o hayan vencido y devuelve el índice
// completo. Los vigentes se resuelven desde localStorage, sin request.
const useCrontuLinks = (resources = []) => {
  const [items, setItems] = useState(getItems);
  const accessToken = getAccessToken();

  // Dispara la revisión periódica: cada tick vuelve a correr el efecto, que decide si
  // hay algo vencido que valga la pena consultar.
  const [tick, setTick] = useState(0);

  // El índice se lee por ref para que el efecto no dependa de él: si dependiera, cada
  // escritura volvería a dispararlo.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const key = resources.map((resource) => resource?.id).filter(Boolean).join(",");

  // Suscripción al índice compartido: lo que escribe una instancia lo ven todas.
  useEffect(() => {
    listeners.add(setItems);
    // Entre el render inicial y la suscripción otra instancia pudo haber escrito.
    setItems(getItems());
    return () => listeners.delete(setItems);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTick((value) => value + 1), REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!REACT_APP_CRONTU_API_URL || !accessToken.value || !key) return;

    const now = Date.now();
    const missing = key.split(",").filter((resourceID) => {
      if (pending.has(resourceID)) return false;
      const entry = itemsRef.current[resourceID];
      return !entry || now - (entry.updatedAt || 0) > REFRESH_MS;
    });

    if (!missing.length) return;
    missing.forEach((resourceID) => pending.add(resourceID));

    let cancelled = false;

    const run = async () => {
      const fresh = {};

      for (let i = 0; i < missing.length; i += BATCH) {
        if (cancelled) break;
        const batch = missing.slice(i, i + BATCH);
        const entries = await Promise.all(
          batch.map(async (resourceID) => {
            const value = await fetchProfile(resourceID, accessToken.value).catch(() => null);
            return [resourceID, value];
          })
        );
        for (const [resourceID, value] of entries) {
          if (value) fresh[resourceID] = { ...value, updatedAt: Date.now() };
        }
      }

      missing.forEach((resourceID) => pending.delete(resourceID));
      if (cancelled || !Object.keys(fresh).length) return;

      writeItems(fresh);
    };

    run();

    return () => {
      // Solo cancela la ESCRITURA. pending NO se libera acá: los requests
      // siguen en vuelo y run() ya los libera al terminar — liberarlos antes
      // de tiempo hacía que una re-corrida del efecto (deps que oscilan,
      // remount) volviera a consultar a los MISMOS profesionales en paralelo,
      // multiplicando la tormenta cuando la API de Crontu está caída.
      cancelled = true;
      // Bug real (auditoría 2026-08-16): acá se borraban los ids de
      // `pending` sin importar si su fetch seguía en vuelo (todavía
      // esperando `fetchProfile`). Si el efecto se volvía a disparar antes
      // de que ese fetch resolviera (el tick de 30min, una rotación de
      // accessToken.value, o un `resources` con los mismos ids) los ids
      // quedaban libres y se volvían a pedir mientras el request original
      // seguía en curso — requests duplicados a la API de Crontu. `run()`
      // ya libera sus propios ids de `pending` al terminar (más abajo,
      // corra o no `cancelled`), así que alcanza con dejar que sea la única
      // que los borra.
    };
  }, [key, accessToken.value, tick]);

  return items;
};

export default useCrontuLinks;
